import type { FilesSectionProps } from "../types/filesSection";
import { URL_FILES } from "../../../constans/apis";
import { downloadFileFetch } from "../../../lib/files/api/fetchFile";
import { useState } from "react";
import { Button, Space, Tooltip, theme, Flex } from "antd";
import {
  DownloadOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

type LoadingState = "idle" | "loading" | "done";

const FilesSection = ({ uuid_xml, uuid_pdf }: FilesSectionProps) => {
  const [xmlState, setXmlState] = useState<LoadingState>("idle");
  const [pdfState, setPdfState] = useState<LoadingState>("idle");
  const { token } = theme.useToken();

  const downloadFile = async (
    uuid: string,
    setState: (s: LoadingState) => void,
  ) => {
    setState("loading");
    try {
      await downloadFileFetch(`${URL_FILES}/${uuid}`, TOKEN_FILES);
      setState("done");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("idle");
    }
  };

  const getButtonProps = (state: LoadingState, variant: "xml" | "pdf") => {
    const isXml = variant === "xml";

    const colorMap = {
      xml: {
        idle: {
          color: token.colorPrimary,
          bg: token.colorPrimaryBg,
          border: token.colorPrimaryBorder,
        },
        loading: {
          color: token.colorPrimary,
          bg: token.colorPrimaryBg,
          border: token.colorPrimaryBorder,
        },
        done: {
          color: token.colorSuccess,
          bg: token.colorSuccessBg,
          border: token.colorSuccessBorder,
        },
      },
      pdf: {
        idle: {
          color: token.colorError,
          bg: token.colorErrorBg,
          border: token.colorErrorBorder,
        },
        loading: {
          color: token.colorError,
          bg: token.colorErrorBg,
          border: token.colorErrorBorder,
        },
        done: {
          color: token.colorSuccess,
          bg: token.colorSuccessBg,
          border: token.colorSuccessBorder,
        },
      },
    };

    const { color, bg, border } = colorMap[variant][state];

    const iconMap = {
      idle: isXml ? <FileTextOutlined /> : <FilePdfOutlined />,
      loading: <DownloadOutlined />,
      done: <CheckCircleOutlined />,
    };

    const labelMap = {
      idle: isXml ? "XML" : "PDF",
      loading: "Descargando…",
      done: "Listo",
    };

    return {
      icon: iconMap[state],
      loading: state === "loading",
      disabled: state === "loading",
      style: {
        color,
        background: bg,
        borderColor: border,
        fontWeight: 600,
        fontSize: 12,
        letterSpacing: "0.02em",
        borderRadius: token.borderRadiusLG,
        transition: "all 0.2s",
      } as React.CSSProperties,
      children: labelMap[state],
    };
  };

  return (
    <Flex justify="flex-end" alignItems="center">
      <Space size="small" className="mb-16">
        <Tooltip title="Descargar XML" placement="top">
          <Button
            size="small"
            {...getButtonProps(xmlState, "xml")}
            onClick={(e) => {
              e.stopPropagation();
              downloadFile(uuid_xml, setXmlState);
            }}
          />
        </Tooltip>

        {uuid_pdf && (
          <Tooltip title="Descargar PDF" placement="top">
            <Button
              size="small"
              {...getButtonProps(pdfState, "pdf")}
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(uuid_pdf, setPdfState);
              }}
            />
          </Tooltip>
        )}
      </Space>
    </Flex>
  );
};

export default FilesSection;
